import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import { api } from '@/lib/api';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Gem, FileText, Printer, Building2, AlertCircle } from 'lucide-react';
import { formatINR, formatCarats, formatDateTime } from '@/lib/format';
import { extractError } from '@/lib/errors';

/**
 * Phase 9.2 — Vendor (on-approval) Return Memo viewer
 *
 * Route: /return-memos/vendor/:memoNo
 * Resolves the memo number via the material-specific endpoint:
 *   - diamond:     GET /api/diamonds/vendor-returns/by-memo/<memoNo>
 *   - color_stone: GET /api/color-stones/vendor-returns/by-memo/<memoNo>
 *   - pearl:       GET /api/pearls/vendor-returns/by-memo/<memoNo>
 *
 * The module is inferred from the `?module=` query string (set by the
 * Memos page) and falls back to a multi-module probe so legacy direct
 * links still resolve.
 */
export default function VendorReturnMemo() {
  const { memoNo } = useParams();
  const navigate = useNavigate();
  return (
    <AppShell
      title="Vendor Return Memo"
      subtitle="On-approval material returned to the vendor."
      right={
        <Button variant="outline" onClick={() => navigate(-1)} className="h-9" data-testid="vendor-return-memo-back">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      }
    >
      <Content memoNo={decodeURIComponent(memoNo)} />
    </AppShell>
  );
}

const MODULE_TO_PREFIX = {
  diamond: 'diamonds',
  color_stone: 'color-stones',
  pearl: 'pearls',
};

async function probeMemo(memoNo, hintedModule) {
  // Order of probes: hinted module first, then the other carat-based
  // modules. Vendor-return memos are unique across modules in practice
  // (memo numbers come from a single shared sequence per vendor), but a
  // probe order is still required so we hit the right module's endpoint
  // and surface the right packet metadata.
  const order = [];
  if (hintedModule && MODULE_TO_PREFIX[hintedModule]) order.push(hintedModule);
  for (const m of ['diamond', 'color_stone', 'pearl']) {
    if (!order.includes(m)) order.push(m);
  }
  let lastErr = null;
  for (const mod of order) {
    const prefix = MODULE_TO_PREFIX[mod];
    try {
      const { data } = await api.get(`/${prefix}/vendor-returns/by-memo/${encodeURIComponent(memoNo)}`);
      return { data, module: mod };
    } catch (e) {
      lastErr = e;
      // Only continue probing on 404s; other status codes are real errors.
      if (e?.response?.status !== 404) throw e;
    }
  }
  throw lastErr || new Error('Vendor return memo not found');
}

function Content({ memoNo }) {
  const [params] = useSearchParams();
  const hintedModule = params.get('module'); // 'diamond' | 'color_stone' | 'pearl' | null
  const [memo, setMemo] = useState(null);
  const [moduleKind, setModuleKind] = useState(hintedModule || 'diamond');
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    probeMemo(memoNo, hintedModule)
      .then(({ data, module }) => {
        if (cancelled) return;
        setMemo(data);
        setModuleKind(module);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(extractError(e, 'Vendor return memo not found'));
      });
    return () => { cancelled = true; };
  }, [memoNo, hintedModule]);

  if (err) return <div className="text-rose-600 py-8" data-testid="vendor-return-memo-error">{err}</div>;
  if (!memo) return <div className="text-slate-500 py-8">Loading…</div>;

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Vendor Return Memo No.</div>
            <div className="text-2xl font-mono text-[var(--ink-navy)]" data-testid="vendor-return-memo-no">{memo.vendor_return_memo_no}</div>
            <div className="text-xs text-slate-500 mt-1">{formatDateTime(memo.timestamp || memo.created_at)} · by <span className="font-medium text-slate-700">{memo.created_by_email}</span></div>
            <div className="text-xs text-slate-500 mt-0.5">Type: <Badge variant="secondary" className="text-[10px]">On-Approval Return</Badge></div>
            {memo.packet_deleted && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-800 text-[11px]" data-testid="vendor-return-memo-packet-tombstoned">
                <AlertCircle size={12} />
                Source approval packet has been auto-cleaned (fully returned). Snapshot below is the authoritative record.
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Returned To Vendor</div>
            {memo.vendor_id ? (
              <Link to={`/vendors/${memo.vendor_id}`} className="font-medium text-[var(--ink-navy)] hover:underline inline-flex items-center gap-1" data-testid="vendor-return-vendor-link">
                <Building2 size={12} />{memo.vendor?.name || memo.vendor_name}
              </Link>
            ) : (
              <span className="font-medium text-[var(--ink-navy)]">{memo.vendor?.name || memo.vendor_name || '—'}</span>
            )}
            <Button variant="outline" className="h-9 mt-2" onClick={() => window.print()} data-testid="vendor-return-memo-print">
              <Printer size={14} className="mr-1.5" /> Print
            </Button>
          </div>
        </div>
        {(memo.reference || memo.note) && (
          <div className="mt-3 text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded">
            {memo.reference && <span><strong>Reference:</strong> {memo.reference}</span>}
            {memo.reference && memo.note && <span> · </span>}
            {memo.note && <span><strong>Note:</strong> {memo.note}</span>}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="text-base font-medium text-[var(--ink-navy)] mb-2">Returned Material</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Packet</th>
                <th className="text-right px-3 py-2">Carats</th>
                <th className="text-right px-3 py-2">Pieces</th>
                <th className="text-right px-3 py-2">Rate (₹/ct)</th>
                <th className="text-right px-3 py-2">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-[var(--divider)]">
                <td className="px-3 py-2">
                  {memo.packet_id
                    ? <Link to={`/diamonds/packets/${memo.packet_id}`} className="inline-flex items-center gap-1 font-mono text-[12px] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-2 py-0.5 rounded hover:underline" data-testid="vendor-return-packet-link">
                        <Gem size={11} />{memo.packet_no}
                      </Link>
                    : <span className="font-mono text-[12px]">{memo.packet_no || '—'}</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono">{formatCarats(memo.carats || 0)}</td>
                <td className="px-3 py-2 text-right font-mono">{memo.pieces || 0}</td>
                <td className="px-3 py-2 text-right font-mono">{(memo.rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                <td className="px-3 py-2 text-right font-mono">{formatINR(memo.value || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="text-right mt-3 text-sm font-mono text-slate-700">
          Total Returned: <span className="text-[var(--ink-navy)] font-medium">{formatINR(memo.value || 0)}</span>
        </div>
      </Card>

      <Card className="p-5">
        <div className="text-base font-medium text-[var(--ink-navy)] mb-2 flex items-center gap-2">
          <FileText size={14} className="text-slate-500" /> Issuer & Receiver Signatures
        </div>
        <div className="grid grid-cols-2 gap-6 mt-4">
          <div>
            <div className="border-b border-slate-300 h-12" />
            <div className="text-xs text-slate-500 mt-1">Issuer (us)</div>
            <div className="text-xs text-slate-700 font-medium">{memo.created_by_email}</div>
          </div>
          <div>
            <div className="border-b border-slate-300 h-12" />
            <div className="text-xs text-slate-500 mt-1">Vendor Acknowledgement</div>
            <div className="text-xs text-slate-700 font-medium">{memo.vendor?.name || memo.vendor_name || '—'}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
